import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/* Detail of a list item and its forms (instead of expanding inside the list), as a dialog in the middle of the screen
   (same pattern as the Sponsor detail, DetailPanel; user request 2026-11-03 — it used to slide in from the right).
   Up to `width` px wide (default 640) and 90% of the screen tall; on phones (≤560px) it rises from the bottom.
   Overlay, Esc / outside click / × close, focus moves in and back to the opener, page scroll locked while open.
   Body scrolls; `footer` stays at the bottom (primary action last). Stays above an open DetailPanel (z-55 > z-50). */

export function Drawer({ open, onClose, title, subtitle, children, footer, width = 640 }: {
    open: boolean
    onClose: () => void
    title: ReactNode
    subtitle?: ReactNode
    children: ReactNode
    footer?: ReactNode
    width?: number
}) {
    const panelRef = useRef<HTMLDivElement>(null)
    const opener = useRef<Element | null>(null)
    const titleId = useId()

    useEffect(() => {
        if (!open) return
        opener.current = document.activeElement
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const first = panelRef.current?.querySelector<HTMLElement>('[data-autofocus], input, select, textarea, button:not([data-close])')
        ;(first ?? panelRef.current)?.focus()
        const onKey = (e: KeyboardEvent) => {
            // Esc fecha só a gaveta (ela pode abrir de dentro de um detalhe, que também fecha com Esc)
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose() }
            if (e.key === 'Tab' && panelRef.current) {
                const f = panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')
                if (!f.length) return
                const a = f[0]!, z = f[f.length - 1]!
                if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus() }
                else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus() }
            }
        }
        document.addEventListener('keydown', onKey, true)
        return () => {
            document.removeEventListener('keydown', onKey, true)
            document.body.style.overflow = prevOverflow
            if (opener.current instanceof HTMLElement) opener.current.focus()
        }
    }, [open, onClose])

    if (!open) return null
    return createPortal(
        <div className="fixed inset-0 z-[55] flex items-center justify-center p-4 max-[560px]:items-end max-[560px]:p-0">
            <div aria-hidden="true" className="absolute inset-0 bg-[rgba(35,35,31,.28)]" onClick={onClose} />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className={cn('relative flex max-h-[90dvh] w-full flex-col overflow-hidden rounded-[16px] border border-line bg-panel outline-none max-[560px]:max-h-[92dvh] max-[560px]:rounded-b-none')}
                style={{ maxWidth: width, boxShadow: '0 24px 64px var(--shadow), 0 4px 12px var(--shadow)' }}
            >
                <div className="flex items-start gap-3 border-b border-line px-5 pt-4 pb-3.5">
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span id={titleId} className="text-[17px] leading-[1.3] font-semibold text-fg">{title}</span>
                        {subtitle && <span className="text-[13px] text-muted">{subtitle}</span>}
                    </div>
                    <button type="button" data-close aria-label="Fechar" onClick={onClose} className="grid size-8 flex-none cursor-pointer place-items-center rounded-[8px] border-0 bg-transparent text-muted hover:bg-[var(--hover)]">
                        <X size={18} aria-hidden="true" />
                    </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
                {footer && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
            </div>
        </div>,
        document.body,
    )
}
